import React, { useRef, useState } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as yup from 'yup';

import api, {
  API_ERRORS,
  getApiErrorMessage,
  getApiErrorName,
} from '../../../../services/api';
import ModalityRulesFields from '../../../../components/ModalityRulesFields';
import {
  hasErrors,
  IModalityRules,
  regulationRules,
  TModalityRulesDraft,
  TModalityRulesErrors,
  validateModalityRules,
} from '../../../../utils/tournament/modalityRules';
import { useToast } from '../../../../hooks/toast';
import Input from '../../../../components/Input';
import Checkbox from '../../../../components/Checkbox';
import PrimaryButton from '../../../../components/Buttons/PrimaryButton';
import SecondaryButton from '../../../../components/Buttons/SecondaryButton';
import { DEFAULT_SCHOOL_TOURNAMENT_MESSAGE } from '../../../../components/Buttons/CreateTournamentButton';

import { FormCard, StepTitle } from '../styles';
import {
  CheckboxGroup,
  CheckboxItemHeader,
  CheckboxItemLabel,
  CheckboxItemWrapper,
  ErrorText,
  FieldLabel,
  FormButtonsRow,
  ModalitySettingsArea,
  StepFormGrid,
  TwoColumnRow,
  WhenGroup,
} from './StepTournament.styles';
import { CrossIcon } from '../../../../assets/svg/SVGComponents';
import SaveIcon from '../../../../assets/svg/SVGComponents/SaveIcon';
import { ITournamentSetupData, MODALITIES, TModalitySlug } from '..';

interface IStepTournamentProps {
  data: ITournamentSetupData;
  onCancel: () => void;
  onSave: (tournament: ITournamentSetupData) => void;
}

interface IFormData {
  name: string;
  date: string;
  end_date: string;
}

/** Só os campos de regra que vieram preenchidos (o resto fica no padrão). */
const pickRules = (
  source?: Partial<Record<keyof IModalityRules, number | null>>,
): Partial<TModalityRulesDraft> => {
  const picked: Partial<TModalityRulesDraft> = {};
  if (!source) return picked;
  (
    [
      'time_limit_seconds',
      'max_rounds',
      'bonus_time_seconds',
      'bonus_points',
    ] as (keyof IModalityRules)[]
  ).forEach(key => {
    const value = source[key];
    if (value !== null && value !== undefined) picked[key] = value;
  });
  return picked;
};

/** Normalize any ISO/date-only string to the datetime-local input format YYYY-MM-DDTHH:MM */
const toDatetimeLocal = (value?: string | null): string => {
  if (!value) return '';
  if (!value.includes('T')) return `${value}T00:00`;
  return value.slice(0, 16);
};

const getNowLocal = () => {
  const now = new Date();
  now.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const buildTournamentSchema = (isEdit: boolean) =>
  yup.object().shape({
    name: yup.string().required('É necessário adicionar um título'),
    date: isEdit
      ? yup.string().required('Informe a data de início')
      : yup
          .string()
          .required('Informe a data de início')
          .test(
            'is-future-start',
            'A data de início deve ser no futuro.',
            value => {
              if (!value) return true;
              return new Date(value) > new Date();
            },
          ),
    end_date: yup
      .string()
      .required('Informe a data de término')
      .test(
        'min-duration',
        'O evento deve ter pelo menos 1 hora de duração.',
        function testMinDuration(value) {
          const { date } = this.parent as { date: string };
          if (!value || !date) return true;
          return (
            new Date(value).getTime() - new Date(date).getTime() >=
            60 * 60 * 1000
          );
        },
      ),
    modalities: yup
      .array()
      .of(yup.string())
      .min(1, 'Selecione pelo menos uma modalidade')
      .required(),
  });

const StepTournament: React.FC<IStepTournamentProps> = ({
  data,
  onCancel,
  onSave,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const tournamentSchema = React.useMemo(
    () => buildTournamentSchema(!!data.id),
    [data.id],
  );
  const [modalitiesError, setModalitiesError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModalities, setSelectedModalities] = useState<TModalitySlug[]>(
    ['robo_pintor', 'seguidor_de_linha'],
  );

  // Valores da modalidade: os do torneio (edição), senão o padrão do
  // regulamento devolvido pelo setup, senão o padrão local.
  const [modalitySettings, setModalitySettings] = useState<
    Record<TModalitySlug, TModalityRulesDraft>
  >(() => {
    const initial = {} as Record<TModalitySlug, TModalityRulesDraft>;
    MODALITIES.forEach(({ slug }) => {
      const fromSetup = data.modality_defaults?.find(d => d.modality === slug);
      initial[slug] = { ...regulationRules(slug), ...pickRules(fromSetup) };
    });
    (data.modalities ?? []).forEach(m => {
      if (m.modality in initial) {
        const slug = m.modality as TModalitySlug;
        initial[slug] = { ...initial[slug], ...pickRules(m) };
      }
    });
    return initial;
  });

  // Validação ao vivo (mesmas regras do backend), não só no envio.
  const modalitySettingsErrors = React.useMemo(() => {
    const errors: Partial<Record<TModalitySlug, TModalityRulesErrors>> = {};
    selectedModalities.forEach(slug => {
      const slugErrors = validateModalityRules(modalitySettings[slug]);
      if (hasErrors(slugErrors)) errors[slug] = slugErrors;
    });
    return errors;
  }, [modalitySettings, selectedModalities]);

  const toggleModality = (slug: TModalitySlug) => {
    if (isLoading) return;
    setSelectedModalities(prev =>
      prev.includes(slug) ? prev.filter(m => m !== slug) : [...prev, slug],
    );
  };

  const handleSubmit = async (formData: IFormData) => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      formRef.current?.setErrors({});
      setModalitiesError(null);

      // Campos da modalidade já mostram o erro ao vivo; aqui só barra o envio.
      if (Object.keys(modalitySettingsErrors).length > 0) {
        return;
      }

      await tournamentSchema.validate(
        { ...formData, modalities: selectedModalities },
        { abortEarly: false },
      );

      const modalitiesPayload = selectedModalities.map(slug => ({
        modality: slug,
        time_limit_seconds: modalitySettings[slug].time_limit_seconds,
        max_rounds: modalitySettings[slug].max_rounds,
        bonus_time_seconds: modalitySettings[slug].bonus_time_seconds,
        bonus_points: modalitySettings[slug].bonus_points,
      }));

      const patch: Record<string, unknown> = {};
      if (formData.name !== data.name) patch.name = formData.name;
      if (formData.date !== toDatetimeLocal(data.date))
        patch.date = formData.date;
      if (formData.end_date !== toDatetimeLocal(data.end_date))
        patch.end_date = formData.end_date;
      patch.modalities = modalitiesPayload;

      const payload = data.id
        ? Object.keys(patch).length > 0
          ? await api.patch(`/tournaments/${data.id}`, patch)
          : { data: data }
        : await api.post('/tournaments', {
            name: formData.name,
            school_id: data.school_id,
            season_id: data.season_id,
            modalities: modalitiesPayload,
            date: formData.date,
            end_date: formData.end_date,
          });

      onSave({
        ...payload.data,
        date: formData.date,
        end_date: formData.end_date,
      });
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const fieldErrors: Record<string, string> = {};
        const items = err.inner.length > 0 ? err.inner : [err];
        items.forEach(e => {
          if (e.path === 'modalities') {
            setModalitiesError(e.message);
          } else if (e.path && !fieldErrors[e.path]) {
            fieldErrors[e.path] = e.message;
          }
        });
        formRef.current?.setErrors(fieldErrors);
      } else if (
        getApiErrorName(err) === API_ERRORS.INVALID_BONUS_TIME ||
        getApiErrorName(err) === API_ERRORS.INVALID_BONUS_POINTS
      ) {
        addToast({
          type: 'error',
          expression: 'Confira o bônus',
          description: getApiErrorMessage(err),
        });
      } else if (
        getApiErrorName(err) === API_ERRORS.TOURNAMENT_DEFAULT_SCHOOL
      ) {
        addToast({
          type: 'error',
          expression: 'Ops!',
          description: DEFAULT_SCHOOL_TOURNAMENT_MESSAGE,
        });
      } else {
        addToast({
          type: 'error',
          expression: 'Erro ao criar torneio',
          description: 'Ocorreu um erro ao enviar os dados. Tente novamente.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard>
      <StepTitle>Etapa 1: Configuração do torneio</StepTitle>

      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={{
          name: data.name,
          school: data.school?.name ?? '',
          date: toDatetimeLocal(data.date),
          end_date: toDatetimeLocal(data.end_date),
        }}
      >
        <StepFormGrid>
          <div>
            <FieldLabel>Título</FieldLabel>
            <Input
              name="name"
              readOnly={isLoading}
              placeholder="Ex: Torneio Regional de Robótica"
            />
          </div>

          <div>
            <FieldLabel>Escola</FieldLabel>
            <Input name="school" disabled readOnly />
          </div>

          {/* Quando */}
          <div>
            <FieldLabel>Quando:</FieldLabel>
            <TwoColumnRow>
              <WhenGroup>
                <FieldLabel>Início do evento</FieldLabel>
                <Input
                  name="date"
                  readOnly={isLoading}
                  type="datetime-local"
                  min={getNowLocal()}
                />
              </WhenGroup>
              <WhenGroup>
                <FieldLabel>Fim do evento</FieldLabel>
                <Input
                  name="end_date"
                  type="datetime-local"
                  readOnly={isLoading}
                  min={data.date || getNowLocal()}
                />
              </WhenGroup>
            </TwoColumnRow>
          </div>

          {/* Modalidades */}
          <div>
            <FieldLabel>Modalidades do Torneio</FieldLabel>
            <CheckboxGroup>
              {MODALITIES.map(({ label, slug }) => {
                const checked = selectedModalities.includes(slug);
                const settingsErr = modalitySettingsErrors[slug];
                return (
                  <CheckboxItemWrapper
                    key={slug}
                    $checked={checked}
                    $expanded={checked}
                    onClick={checked ? undefined : () => toggleModality(slug)}
                  >
                    <CheckboxItemHeader onClick={() => toggleModality(slug)}>
                      <div onClick={e => e.stopPropagation()}>
                        <Checkbox
                          disabled={isLoading}
                          checked={checked}
                          onChange={() => toggleModality(slug)}
                        />
                      </div>
                      <CheckboxItemLabel>{label}</CheckboxItemLabel>
                    </CheckboxItemHeader>
                    {checked && (
                      <ModalitySettingsArea onClick={e => e.stopPropagation()}>
                        <ModalityRulesFields
                          modality={slug}
                          value={modalitySettings[slug]}
                          errors={settingsErr}
                          disabled={isLoading}
                          onChange={next =>
                            setModalitySettings(prev => ({
                              ...prev,
                              [slug]: next,
                            }))
                          }
                        />
                      </ModalitySettingsArea>
                    )}
                  </CheckboxItemWrapper>
                );
              })}
            </CheckboxGroup>
            {modalitiesError && <ErrorText>{modalitiesError}</ErrorText>}
          </div>
        </StepFormGrid>

        <FormButtonsRow>
          <SecondaryButton
            type="button"
            onClick={onCancel}
            disabled={isLoading}
          >
            <CrossIcon
              width="1rem"
              height="1rem"
              stroke="#323232"
              strokeWidth={1.5}
            />
            Cancelar
          </SecondaryButton>
          <PrimaryButton type="submit" loading={isLoading} disabled={isLoading}>
            <SaveIcon
              width="1.6rem"
              fill="none"
              stroke="#fff"
              strokeWidth={2.5}
            />
            Salvar
          </PrimaryButton>
        </FormButtonsRow>
      </Form>
    </FormCard>
  );
};
export { StepTournament };
